import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { fetchCurrentUser, UserApiResponse } from '../services/userService';

interface UserContextType {
    userData: UserApiResponse | null;
    loading: boolean;
    error: Error | null;
    refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [userData, setUserData] = useState<UserApiResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const loadUserData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Always fetch from API to ensure correct data format
            // The API returns properly nested format with role, team, client objects
            const response = await fetchCurrentUser();
            setUserData(response);
        } catch (err) {
            console.error('Failed to fetch user data:', err);
            setError(err as Error);
        } finally {
            setLoading(false);
        }
    };

    // Load user data on mount
    useEffect(() => {
        loadUserData();
    }, []);

    const refreshUser = async () => {
        await loadUserData();
    };

    return (
        <UserContext.Provider value={{ userData, loading, error, refreshUser }}>
            {children}
        </UserContext.Provider>
    );
};

// Custom hook to use the user context
export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};
